<template>
  <div
    v-show="isVisible"
    :class="$style.container"
  >
    <img
      :alt="emptyStateConfig.altText"
      :class="$style.emptyStateImage"
      data-test-id="image"
      :src="emptyStateConfig.image"
      @load="handleImgLoaded"
    >
    <h1
      data-test-id="title"
    >
      {{ emptyStateConfig.title }}
    </h1>
    <p
      data-test-id="description"
    >
      {{ emptyStateConfig.description }}
    </p>
    <p>
      Learn more about this state in the
      <a
        data-test-id="learnMoreLink"
        :href="KbArticleUrls.managingUserStates"
        rel="noopener noreferrer"
        target="_blank"
      >Managing User States article</a>
    </p>
  </div>
</template>

<script>
import { ItemTypeConsts, ModuleConsts, StoreModuleMap } from 'client/util/Constants';
import { S3BucketAddress } from 'client/util/Constants/ApplicationConstants';
import { capitalize, get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';

const organizationsCollection = StoreModuleMap[ItemTypeConsts.organizations];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

const types = {
  active: ModuleConsts.systemUser.accountStates.activated,
  staged: ModuleConsts.systemUser.accountStates.staged,
  suspended: ModuleConsts.systemUser.accountStates.suspended,
};

export default {
  name: 'EmptyFilteredUserState',

  props: {
    state: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      KbArticleUrls,
      S3BucketAddress,
      hasImageLoaded: false,
    };
  },

  computed: {
    ...mapGetters(organizationsCollection, ['getItemDetails']),

    ...mapState(settingsModule, ['orgId']),

    emptyStateConfig() {
      const stateInformation = {
        altText: '',
        description: '',
        image: '',
        title: '',
        userCount: 0,
      };

      switch (this.state) {
        case types.active:
          stateInformation.altText = 'active zero state graphic';
          stateInformation.description = this.getText(this.state);
          stateInformation.image = `${this.S3BucketAddress}/images/active-zero-state.svg`;
          stateInformation.title = 'No active users';
          stateInformation.userCount = get(this.getItemDetails(this.orgId), 'systemUsersActivatedCount', 0);
          break;
        case types.staged:
          stateInformation.altText = `${this.state.toLowerCase()} zero state graphic`;
          stateInformation.description = this.getText(this.state);
          stateInformation.image = `${this.S3BucketAddress}/images/staged-zero-state.svg`;
          stateInformation.title = `No ${this.state.toLowerCase()} users`;
          stateInformation.userCount = get(this.getItemDetails(this.orgId), 'systemUsersStagedCount', 0);
          break;
        case types.suspended:
          stateInformation.altText = `${this.state.toLowerCase()} zero state graphic`;
          stateInformation.description = this.getText(this.state);
          stateInformation.image = `${this.S3BucketAddress}/images/suspended-zero-state.svg`;
          stateInformation.title = `No ${this.state.toLowerCase()} users`;
          stateInformation.userCount = get(this.getItemDetails(this.orgId), 'systemUsersSuspendedCount', 0);
          break;
        default:
          break;
      }

      return stateInformation;
    },

    /**
     * Show the empty state if there are no users in the state and the image has finished loading
     */
    isVisible() {
      const { userCount } = this.emptyStateConfig;

      return !userCount && this.hasImageLoaded;
    },
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions(organizationsCollection, ['getDetailsForOrganization']),

    getText(state) {
      let text = '';

      if (state === types.active) {
        text = 'Active users have access to their JumpCloud provided resources. New users are created in the Active User State.  Suspended users can be activated at any time. Active accounts count towards your total number of billable users.';
      }

      if (state === types.suspended) {
        text = `${capitalize(state)} users cannot access their JumpCloud provided resources, but their user data and configuration records are maintained. ${capitalize(state)} users are retained in the system while they are ${state.toLowerCase()} and billed as normal users.`;
      }

      if (state === types.staged) {
        text = 'Use this state to easily identify users being onboarded before their start date. Assign all the resources they will need Day One. Accounts will be provisioned but no access will be granted. Make this the default state for all new users or for users created using a specific method from the Settings page.';
      }
      return text;
    },

    handleImgLoaded() {
      this.hasImageLoaded = true;
      this.setLoadingOverlay(false);
    },

    async handleMounted() {
      this.setLoadingOverlay(true);
      await this.getDetailsForOrganization({ id: this.orgId });
    },

    // Use the loadingOverlay from ItemTable
    setLoadingOverlay(value) {
      this.$parent.setLoadingOverlay(value);
    },
  },
};
</script>

<style module>
.container {
  align-self: center;
  display: grid;
  grid-row-gap: 0.5rem;
  justify-items: center;
  max-width: 43rem;
  text-align: center;
}

.emptyStateImage {
  height: 20rem;
  margin-top: 7rem;
  width: 20rem;
}
</style>
